<template>
  <v-layout>
    <v-flex xs12 offset-sm2 sm8 offset-md0 md12>
      <template v-if="organization.id">
        <h1 class="title-page">
          <v-icon class="material-icons-outlined">home</v-icon>
          <br />{{ organization.name }}
        </h1>

        <template v-if="salesManagements">
          <template v-if="!hasPhotoSalesManagement()">
            <v-layout align-center column mb-3>
              <div
                class="cardbox"
                style="display: flex; flex-direction: row; flex-wrap: wrap;"
              >
                <p class="mb-0">
                  現在、写真が販売されていません。次回の写真販売開始までお待ちください。
                </p>
                <p>
                  ※園・施設に所属されていた場合、販売が開始されたら閲覧・購入可能となります。
                </p>
                <p class="mb-0">
                  お子さまの登録をしていない場合は写真の販売期間中であっても表示されない場合があります。
                </p>
                <p>
                  子どもを追加する場合は<a
                    href="/site/kids/new"
                    target="_blank"
                    rel="noopener noreferrer"
                    >こちら</a
                  >から。入園前の場合は登録済みでも表示されていません。
                </p>
              </div>
            </v-layout>
          </template>
          <template>
            <v-layout align-center column>
              <template v-for="sm in salesManagements">
                <!-- combookBinding の場合 -->
                <router-link
                  v-if="isCombookBinding(sm)"
                  :key="sm.id"
                  :to="getCombookBindingRouterParams()"
                  class="mb-3 cardbox"
                >
                  <div>
                    <div class="sales-card-header mb-2">
                      <v-icon small color="primary">
                        import_contacts
                      </v-icon>
                      <span>連絡帳製本</span>
                    </div>

                    <h2 class="font-weight-bold sales-title-text mb-2">
                      連絡帳を製本
                    </h2>

                    <div class="sales-period-text">
                      <p class="mb-0">
                        連絡帳の日々の記録を製本できます。
                      </p>
                    </div>

                    <div
                      v-for="notice in sm.notices"
                      :key="notice"
                      class="mt-2"
                    >
                      <span class="notice-text">{{ notice }}</span>
                    </div>
                  </div>
                  <v-icon medium color="primary">navigate_next</v-icon>
                </router-link>

                <!-- それ以外の場合 -->
                <router-link
                  v-else
                  :key="sm.id"
                  :to="getRouterParams(sm)"
                  class="mb-3 cardbox"
                >
                  <div>
                    <div class="sales-card-header mb-2">
                      <v-icon small color="primary">
                        {{ getIconName(sm) }}
                      </v-icon>
                      <span>{{ getSalesManagementTypeName(sm) }}</span>
                    </div>

                    <h2 class="font-weight-bold sales-title-text mb-2">
                      {{ sm.title }}
                    </h2>

                    <div class="sales-period-text">
                      <p class="mb-0">
                        販売期間：{{ sm.formattedSalesStartDate }}〜{{
                          sm.formattedSalesEndDate
                        }}
                      </p>
                    </div>
                    <div
                      v-for="notice in sm.notices"
                      :key="notice"
                      class="mt-2"
                    >
                      <span class="notice-text">{{ notice }}</span>
                    </div>
                  </div>
                  <v-icon medium color="primary">navigate_next</v-icon>
                </router-link>
              </template>
            </v-layout>
          </template>
        </template>
      </template>
    </v-flex>
    <terms-and-policy
      v-if="agreementVersions"
      :agreement-versions="agreementVersions"
    />
  </v-layout>
</template>

<script>
import { mapState } from "vuex";

import ApiErrorHandler from "../../mixins/ApiErrorHandler";
import { API_ERROR_CODE_NOT_FOUND_FOR_SELECTED_FAMILY } from "../../lib/lookmee_photo/ApiErrorCodes";

import { SALES_TYPES } from "../../data/constants";
import TermsAndPolicy from "../../components/TermsAndPolicy.vue";

export default {
  components: {
    "terms-and-policy": TermsAndPolicy
  },
  mixins: [ApiErrorHandler],
  props: {
    organizationId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      salesManagements: null,
      agreementVersions: undefined
    };
  },
  computed: {
    ...mapState({
      apiClient: "apiClient",
      organization: "organization"
    })
  },
  async created() {
    await this.fetchInitData();

    // For Karte
    const hasSale = this.salesManagements.length > 0;
    const isfreePostageCampaign = hasSale
      ? this.salesManagements[0].freePostageCampaign
      : undefined;

    if (this.organization.id) {
      dataLayer.push({
        en_id: this.organization.id,
        onsale: hasSale,
        // postage_free: Is there a free postage campaign?
        postage_free: isfreePostageCampaign
      });
    }
  },
  methods: {
    async fetchInitData() {
      const organizationId = this.organizationId;

      this.$store.dispatch("startLoading");
      try {
        await this.$store.dispatch("organization/fetchOrganization", {
          organizationId
        });
        this.salesManagements = await this.apiClient.getSalesManagements(
          organizationId
        );
        this.agreementVersions = await this.apiClient.getAgreementVersions();
      } catch (errors) {
        if (
          errors.status === 404 &&
          errors.getErrorCode() === API_ERROR_CODE_NOT_FOUND_FOR_SELECTED_FAMILY
        ) {
          this.$router.push({ name: "notFoundForFamily" });
        } else {
          this.handleApiErrors(errors, {
            store: this.$store,
            router: this.$router,
            sentry: this.sentry
          });
        }
      } finally {
        this.$store.dispatch("endLoading");
      }
    },
    getIconName(salesManagement) {
      switch (salesManagement.salesType.value) {
        case SALES_TYPES.photoAndVideo:
          return "photo_camera";
        case SALES_TYPES.photoBook:
          return "fas fa-book";
        default:
          return "";
      }
    },
    getSalesManagementTypeName(salesManagement) {
      if (salesManagement.salesType.value === SALES_TYPES.photoBook) {
        return "フォトブック";
      } else {
        return "写真販売";
      }
    },
    getRouterParams(salesManagement) {
      switch (salesManagement.salesType.value) {
        case SALES_TYPES.photoAndVideo:
          return {
            name: "organizationMain",
            params: {
              organizationId: this.organization.id,
              salesManagementId: salesManagement.id
            }
          };
        case SALES_TYPES.photoBook:
          return {
            name: "organizationSelectPhotoBookType",
            params: {
              organizationId: this.organization.id,
              salesManagementId: salesManagement.id
            }
          };
        default:
          return "";
      }
    },
    getCombookBindingRouterParams() {
      return {
        name: "organizationCombookBinding",
        params: {
          organizationId: this.organization.id
        }
      };
    },
    isCombookBinding(salesManagement) {
      return salesManagement.salesType.value === SALES_TYPES.combookBinding;
    },
    hasPhotoSalesManagement() {
      return this.salesManagements.some(
        sm =>
          sm.salesType.value === SALES_TYPES.photoAndVideo ||
          sm.salesType.value === SALES_TYPES.photoBook
      );
    }
  }
};
</script>

<style scoped>
.cardbox {
  text-decoration: none;
  background: white;
  color: black;
  border-radius: 8px;
  box-shadow: 0px 1px 4px 0px #00000029;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 10px 20px 16px;
  gap: 8px;
  width: 443px;
}
/* For mobile device */
@media (max-width: 600px) {
  .cardbox {
    width: 100%;
  }
}
a.cardbox:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
.sales-card-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: bold;
  color: #4cb5b8;
}
.sales-title-text {
  font-size: 18px;
}
.sales-period-text {
  font-size: 13px;
}
.notice-text {
  background-color: #f9b73a;
  color: white;
  font-weight: bold;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 13px;
}
</style>
